import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  alpha,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../context/AuthContext';
import LogoSection from '../components/common/LogoSection';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    const result = await login(formData.email, formData.password);

    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Panel – Branding */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #061527 0%, #0C2340 50%, #183B63 100%)',
          position: 'relative',
          overflow: 'hidden',
          p: 6,
        }}
      >
        <Box sx={{ position: 'absolute', top: '15%', left: '10%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)' }} />
        <Box sx={{ position: 'absolute', bottom: '10%', right: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(12,35,64,0.3) 0%, transparent 70%)' }} />

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <LogoSection
            size="large"
            textSx={{ fontSize: '1.4rem' }}
            sx={{ flexDirection: 'column', mb: 3, justifyContent: 'center' }}
          />
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.86)', fontWeight: 400, maxWidth: 320, lineHeight: 1.6 }}>
            Admin Panel — Manage your travel agency platform.
          </Typography>
        </Box>
      </Box>

      {/* Right Panel – Form */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 480px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#FCFAF4',
          p: { xs: 3, sm: 5 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 4 }}>
            <LogoSection size="small" textSx={{ fontSize: '0.95rem' }} />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                bgcolor: alpha('#D4AF37', 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 26, color: '#D4AF37' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0C2340', mb: 0.5, letterSpacing: -0.5 }}>
              Admin Login
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to manage your travel agency platform
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoFocus
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)',
                boxShadow: '0 10px 28px rgba(12,35,64,0.28)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #183B63 0%, #0C2340 100%)',
                  boxShadow: '0 14px 36px rgba(12,35,64,0.38)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': { background: alpha('#0C2340', 0.3), boxShadow: 'none', color: alpha('#fff', 0.5) },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;